import React, { useState, useEffect } from 'react';
import { Logo } from '../components/Logo';
import { LoginForm } from '../components/LoginForm';
import { ChangePassword } from '../components/ChangePassword';
import { ForgotPasswordForm } from '../components/ForgotPasswordForm';

interface LoginPageProps {
  onLogin: (email: string, password: string) => Promise<void>;
  onPasswordChanged: (email: string, currentPassword: string, newPassword: string) => Promise<void>;
  forcePasswordChange?: boolean;
  initialEmail?: string;
}

export const LoginPage: React.FC<LoginPageProps> = ({ 
  onLogin, 
  onPasswordChanged, 
  forcePasswordChange = false, 
  initialEmail = '' 
}) => {
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [email, setEmail] = useState('');
  
  // Get branding from environment variables
  const appName = import.meta.env.VITE_APP_NAME || 'NEXOS';
  const appDescription = import.meta.env.VITE_APP_DESCRIPTION || 'Sistema de Gestão';
  const companyName = import.meta.env.VITE_COMPANY_NAME || 'Sua Empresa';
  const currentYear = new Date().getFullYear();

  useEffect(() => {
    if (forcePasswordChange) {
      setEmail(initialEmail);
      setShowChangePassword(true);
    } else {
      setShowChangePassword(false);
    }
  }, [forcePasswordChange, initialEmail]);

  const handleLogin = async (loginEmail: string, loginPassword: string) => {
    try {
      await onLogin(loginEmail, loginPassword);
    } catch (error) {
      console.error('Erro no login:', error);
      alert('Erro ao fazer login. Tente novamente.');
    }
  };

  const handlePasswordChange = async (currentPassword: string, newPassword: string) => {
    try {
      await onPasswordChanged(email, currentPassword, newPassword);
      setShowChangePassword(false);
    } catch (error: any) {
      console.error('Erro ao alterar senha:', error);
      alert(error?.message || 'Erro ao alterar senha. Tente novamente.');
    }
  };

  const handleCancelChangePassword = () => {
    setShowChangePassword(false);
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col justify-center py-12 px-4 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md md:max-w-lg">
        <div className="bg-white py-8 px-6 shadow-lg rounded-lg">
          {/* Logo */}
          <div className="flex justify-center mb-6">
            <Logo className="h-16" />
          </div>

          {/* Título */}
          <div className="text-center mb-8">
            <h2 className="text-3xl font-bold text-gray-900">
              {appDescription}
            </h2>
            <p className="mt-2 text-sm text-gray-600">
              {showChangePassword
                ? 'Alterar sua senha de acesso'
                : showForgotPassword
                ? 'Recuperar acesso à sua conta'
                : 'Faça login para acessar o sistema'
              }
            </p>
          </div>

          {/* Formulário */}
          {showChangePassword ? (
            <ChangePassword
              username={email}
              onSubmit={handlePasswordChange}
              onCancel={handleCancelChangePassword}
            />
          ) : showForgotPassword ? (
            <ForgotPasswordForm onBack={() => setShowForgotPassword(false)} />
          ) : (
            <LoginForm
              onSubmit={handleLogin}
              onForgotPassword={() => setShowForgotPassword(true)}
              isFirstLogin={false}
            />
          )}
        </div>

        {/* Rodapé */}
        <div className="mt-6 text-center">
          <p className="text-xs text-gray-500">
            © {currentYear} {companyName}. Todos os direitos reservados.
          </p>
        </div>
      </div>
    </div>
  );
};
